import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import bowtieService from './bowtieService'

//set initial state for SINGLE bowtie (editor)
const initialState = {
    data: null,               
    isError: false,
    isSuccess: false,
    isLoading: false,
    notFound: false,          
    message: ''
}

//get single bowtie (singular - for editor)
export const getBowtie = createAsyncThunk(
    'bowtie/getOne', 
    async (id, thunkAPI) => {
    try {
        const token = thunkAPI.getState().auth.user.token
        return await bowtieService.getBowtie(id, token)
    } catch (error) {
        const message = (error.response && 
            error.response.data && 
            error.response.data.message) || 
            error.message || 
            error.toString()
        
        const isNotFound = error.response?.status === 404 || 
                          message.toLowerCase().includes('not found') ||
                          message.toLowerCase().includes('404')
        
        return thunkAPI.rejectWithValue({ message, isNotFound })
    }
})

//create bowtie (for editor - quick create)
export const createBowtie = createAsyncThunk(
    'bowtie/create',
    async (bowtieData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await bowtieService.createBowtie(bowtieData, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//update bowtie (for editor)
export const updateBowtie = createAsyncThunk(
    'bowtie/update',
    async (bowtieData, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await bowtieService.updateBowtie(bowtieData, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//toggle viewable flag
export const setViewable = createAsyncThunk(
    'bowtie/setViewable',
    async ({ id, viewable }, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await bowtieService.setViewable(id, viewable, token)
        } catch (error) {
            const message = (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//delete bowtie (for editor)
export const deleteBowtie = createAsyncThunk(
    'bowtie/delete',
    async (id, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await bowtieService.deleteBowtie(id, token)
        } catch (error) {
            const message = (error.response && 
                error.response.data && 
                error.response.data.message) || 
                error.message || 
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//Singular Bowtie Slice (for editor)
export const bowtieSlice = createSlice({
    name: 'bowtie',
    initialState,
    reducers: {
        reset: (state) => initialState,
        setThreatSum: (state, action) => {
            if (state.data) state.data.threatSum = action.payload
        },
        setRisk: (state, action) => {
            if (state.data) state.data.risk = action.payload
        },
        clearBowtie: (state) => {
            state.data = null
            state.isError = false
            state.isSuccess = false
            state.notFound = false
            state.message = ''
            state.isLoading = false
        },
        // multiuser.md Phase 2c: applied from a socket broadcast, not a thunk.
        remoteBowtieUpdated: (state, action) => {
            if (state.data && state.data._id === action.payload._id) {
                Object.assign(state.data, action.payload)
            }
        },
        // Patches from the shared bowtie:metrics broadcast (riskCalculator.js).
        remoteBowtieMetricsUpdated: (state, action) => {
            if (state.data && state.data._id === action.payload.bowtieId) {
                state.data.threatSum = action.payload.threatSum
                state.data.risk = action.payload.risk
            }
        },
    },
    extraReducers: (builder) => {
        builder
        // GET BOWTIE
        .addCase(getBowtie.pending, (state) => {
            state.isLoading = true
            state.isError = false
            state.notFound = false
            state.message = ''
        })
        .addCase(getBowtie.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false
            state.notFound = false
            state.message = ''
            state.data = action.payload
        })
        .addCase(getBowtie.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false
            state.notFound = action.payload?.isNotFound || false
            state.message = action.payload?.message || action.payload
            state.data = null
        })
        // CREATE BOWTIE
        .addCase(createBowtie.pending, (state) => {
            state.isLoading = true
            state.isError = false
            state.message = ''
        })
        .addCase(createBowtie.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false
            state.message = ''
            state.data = action.payload  // Set as current bowtie
        })
        .addCase(createBowtie.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false
            state.message = action.payload
        })
        // UPDATE BOWTIE
        .addCase(updateBowtie.pending, (state) => {
            state.isLoading = true
            state.isError = false
            state.message = ''
        })
        .addCase(updateBowtie.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false
            state.message = ''
            state.data = action.payload
        })
        .addCase(updateBowtie.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false
            state.message = action.payload
        })
        // SET VIEWABLE
        .addCase(setViewable.fulfilled, (state, action) => {
            if (state.data) state.data.viewable = action.payload.viewable
        })
        // DELETE BOWTIE
        .addCase(deleteBowtie.pending, (state) => {
            state.isLoading = true
            state.isError = false
            state.message = ''
        })
        .addCase(deleteBowtie.fulfilled, (state, action) => {
            state.isLoading = false
            state.isSuccess = true
            state.isError = false
            state.message = 'Bowtie deleted successfully'
            state.data = null  // Clear current bowtie
        })
        .addCase(deleteBowtie.rejected, (state, action) => {
            state.isLoading = false
            state.isError = true
            state.isSuccess = false
            state.message = action.payload
        })
    }
})

export const { reset, clearBowtie, setThreatSum, setRisk, remoteBowtieUpdated, remoteBowtieMetricsUpdated } = bowtieSlice.actions
export default bowtieSlice.reducer